import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Paper, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getRunStatus } from '../../../utilityFunctions/dashboardApi';
import HitlReviewPanel from '../../llm/HitlReviewPanel';
import EditDiffPreview from './EditDiffPreview';
import { stageLabel } from './stageLabels';

export const RUN_POLL_MS = 2500; // same cadence as NlqChatWorkspace TURN_POLL_MS

export { stageLabel } from './stageLabels';

const VERDICT_COLOR = { VALID: 'success', DEGRADED: 'warning', FAILED: 'error', RUNNING: 'primary', PENDING: 'default' };
const STATUS_COLOR = { PENDING: 'default', RUNNING: 'primary', AWAITING_HITL: 'warning', COMPLETED: 'success', FAILED: 'error' };
const TERMINAL = ['COMPLETED', 'FAILED', 'AWAITING_HITL'];

/** One card per widget in progress[] — rendered while the run builds (progressive). */
function WidgetProgressCard({ item }) {
  const { t } = useTranslation();
  const verdict = item.verdict || 'PENDING';
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, minHeight: 92, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5,
      borderColor: verdict === 'FAILED' ? 'error.light' : undefined, opacity: verdict === 'PENDING' ? 0.7 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} title={item.title}>
          {item.title || item.widget_id}
        </Typography>
        <Chip size="small" color={VERDICT_COLOR[verdict] || 'default'} variant={verdict === 'PENDING' ? 'outlined' : 'filled'}
          label={t(`Dashboards.run.widgetVerdict.${verdict}`, { defaultValue: verdict })} />
      </Box>
      {verdict !== 'VALID' && verdict !== 'DEGRADED' && (
        <Typography variant="caption" color="text.secondary">{stageLabel(t, item.stage)}</Typography>
      )}
      {verdict === 'RUNNING' && <LinearProgress sx={{ borderRadius: 1 }} />}
      {item.status_note && (
        <Typography variant="caption" color={verdict === 'FAILED' ? 'error' : 'warning.dark'} sx={{ overflowWrap: 'anywhere' }}>{item.status_note}</Typography>
      )}
    </Paper>
  );
}

/**
 * Polls GET /runs/{runId}/pipeline-status every 2.5 s (pollers-Map pattern from
 * NlqChatWorkspace), renders the stage / per-widget progress, and hosts the HITL
 * checkpoint: DASHBOARD_PLAN inline, EDIT_PLAN in the diff-preview dialog.
 * Polling stops on COMPLETED / FAILED / AWAITING_HITL and resumes once the checkpoint
 * is resolved.
 *
 * @param onCompleted(status)  once, on COMPLETED (status.resultVersionId; null for META edits)
 * @param onFailed(status)     once, on FAILED
 * @param onDismiss()          close button (only shown once the run is terminal)
 */
export default function RunProgressPanel({ runId, jwt, title, showWidgetCards = true, onCompleted, onFailed, onDismiss }) {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(null);
  const [pollError, setPollError] = React.useState(false);
  const [diffOpen, setDiffOpen] = React.useState(true);
  const [notice, setNotice] = React.useState(null);
  const [startedAt] = React.useState(() => Date.now());
  const [now, setNow] = React.useState(() => Date.now());

  const pollersRef = React.useRef(new Map());
  const mountedRef = React.useRef(true);
  const firedRef = React.useRef(false);
  const cbRef = React.useRef({ onCompleted, onFailed });
  cbRef.current = { onCompleted, onFailed };

  const stopPolling = React.useCallback((id) => {
    const intervalId = pollersRef.current.get(id);
    if (intervalId) clearInterval(intervalId);
    pollersRef.current.delete(id);
  }, []);

  const pollOnce = React.useCallback(async (id) => {
    const res = await getRunStatus(id, jwt);
    if (!mountedRef.current) return;
    setNow(Date.now());
    if (!res.ok || !res.data) {
      setPollError(true);
      return;
    }
    setPollError(false);
    const s = res.data;
    setStatus(s);
    if (TERMINAL.includes(s.status)) {
      stopPolling(id);
      if (s.status === 'AWAITING_HITL') setDiffOpen(true);
      if (!firedRef.current && s.status === 'COMPLETED') {
        firedRef.current = true;
        if (cbRef.current.onCompleted) cbRef.current.onCompleted(s);
      }
      if (!firedRef.current && s.status === 'FAILED') {
        firedRef.current = true;
        if (cbRef.current.onFailed) cbRef.current.onFailed(s);
      }
    }
  }, [jwt, stopPolling]);

  const startPolling = React.useCallback((id) => {
    if (!id || pollersRef.current.has(id)) return;
    const intervalId = setInterval(() => pollOnce(id), RUN_POLL_MS);
    pollersRef.current.set(id, intervalId);
    pollOnce(id);
  }, [pollOnce]);

  React.useEffect(() => {
    mountedRef.current = true;
    firedRef.current = false;
    setStatus(null);
    startPolling(runId);
    const pollers = pollersRef.current;
    return () => {
      mountedRef.current = false;
      pollers.forEach((intervalId) => clearInterval(intervalId));
      pollers.clear();
    };
  }, [runId, startPolling]);

  const onHitlResolved = (approved) => {
    setNotice(approved ? t('Dashboards.hitl.approved') : t('Dashboards.hitl.answered'));
    setStatus((s) => (s ? { ...s, status: 'RUNNING', checkpoint: null } : s));
    startPolling(runId);
  };

  const runStatus = (status && status.status) || 'PENDING';
  const progress = (status && Array.isArray(status.progress)) ? status.progress : [];
  const ready = progress.filter((p) => p.verdict === 'VALID' || p.verdict === 'DEGRADED').length;
  const checkpoint = status && status.status === 'AWAITING_HITL' ? status.checkpoint : null;
  const isEditPlan = checkpoint && checkpoint.currentStage === 'EDIT_PLAN';
  const terminal = runStatus === 'COMPLETED' || runStatus === 'FAILED';
  const elapsedS = Math.max(0, Math.round((now - startedAt) / 1000));
  const isMeta = runStatus === 'COMPLETED' && status && !status.resultVersionId && status.terminalNote;
  const heading = title || (status && status.inputKind === 'NL_CREATE' ? t('Dashboards.run.title') : t('Dashboards.nlEdit.submitting'));

  return (
    // minWidth 0 + overflowWrap 'anywhere' cascade into the HITL plan / diff and the
    // progress cards so long names and notes wrap instead of overflowing to the right.
    <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, mb: 2, borderRadius: 3, bgcolor: '#fff', minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {!terminal && runStatus !== 'AWAITING_HITL' && <CircularProgress size={18} />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>{heading}</Typography>
        <Chip size="small" color={STATUS_COLOR[runStatus] || 'default'} label={t(`Dashboards.run.status.${runStatus}`, { defaultValue: runStatus })} />
        {status && status.currentStage && !terminal && runStatus !== 'AWAITING_HITL' && (
          <Typography variant="body2" color="text.secondary">{stageLabel(t, status.currentStage)}</Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {progress.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('Dashboards.run.widgetsProgress', { done: ready, total: progress.length })}
          </Typography>
        )}
        {!terminal && <Typography variant="caption" color="text.secondary">{t('Dashboards.run.elapsed', { s: elapsedS })}</Typography>}
        {terminal && onDismiss && (
          <Tooltip title={t('Dashboards.common.close')}>
            <IconButton size="small" onClick={onDismiss}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Box>

      {pollError && <Alert severity="warning" sx={{ mb: 1 }}>{t('Dashboards.run.pollFailed')}</Alert>}
      {notice && !terminal && !checkpoint && (
        <Alert severity="info" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>
      )}
      {runStatus === 'FAILED' && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t('Dashboards.run.failed', { note: (status && (status.terminalNote || status.terminalStage)) || '—' })}
        </Alert>
      )}
      {isMeta && (
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{t('Dashboards.nlEdit.metaAnswer')}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{status.terminalNote}</Typography>
        </Alert>
      )}

      {checkpoint && !isEditPlan && (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
          <HitlReviewPanel key={checkpoint.checkpointId} checkpoint={checkpoint} jwtToken={jwt} onResolved={onHitlResolved} />
        </Box>
      )}
      {checkpoint && isEditPlan && (
        <>
          <Alert severity="warning" sx={{ mb: 1 }}
            action={<Button color="inherit" size="small" onClick={() => setDiffOpen(true)}>{t('Dashboards.diff.title')}</Button>}>
            {t('Dashboards.run.status.AWAITING_HITL')}
          </Alert>
          <EditDiffPreview open={diffOpen} checkpoint={checkpoint} jwtToken={jwt}
            onResolved={(approved) => { setDiffOpen(false); onHitlResolved(approved); }}
            onClose={() => setDiffOpen(false)} />
        </>
      )}

      {showWidgetCards && progress.length > 0 && !checkpoint && (
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
          {progress.map((p) => <WidgetProgressCard key={p.widget_id} item={p} />)}
        </Box>
      )}
    </Paper>
  );
}
